// Shared placement for floating boxes (tooltips, filter dropdowns): centred below
// the anchor element, shifted left/right to stay on screen, and flipped above the
// anchor when there isn't room below. Coordinates are viewport-relative, for use
// with `position: fixed`.

export const POPOVER_EDGE_MARGIN = 8;

// anchorRect: the anchor's getBoundingClientRect().
// size: { width, height } of the box.
// options.gap: space between anchor and box.
// options.align: 'center' (default) centres the box on the anchor; 'start' / 'end'
//   line up the box's left / right edge with the anchor's (still shifted to stay
//   on screen).
// Returns { top, left, placement: 'below' | 'above', arrowLeft, maxHeight } where
// arrowLeft is the anchor's centre relative to the box (for an arrow), and
// maxHeight is only set when the box fits neither below nor above, so it should
// scroll within the larger side.
export function placePopover(anchorRect, size, { gap = 6, align = 'center' } = {}) {
  const margin = POPOVER_EDGE_MARGIN;
  const viewportWidth = document.documentElement.clientWidth;
  const viewportHeight = window.innerHeight;
  const { width, height } = size;

  const anchorCentre = anchorRect.left + anchorRect.width / 2;
  const preferredLeft = align === 'end'
    ? anchorRect.right - width
    : (align === 'start' ? anchorRect.left : anchorCentre - width / 2);
  const maxLeft = Math.max(margin, viewportWidth - width - margin);
  const left = Math.min(Math.max(preferredLeft, margin), maxLeft);

  const spaceBelow = viewportHeight - anchorRect.bottom - gap - margin;
  const spaceAbove = anchorRect.top - gap - margin;

  let placement = 'below';
  let maxHeight;
  if (height > spaceBelow) {
    if (height <= spaceAbove) {
      placement = 'above';
    } else {
      // Fits neither way: use the roomier side and let the box scroll.
      placement = spaceAbove > spaceBelow ? 'above' : 'below';
      maxHeight = Math.max(80, placement === 'above' ? spaceAbove : spaceBelow);
    }
  }
  const boxHeight = maxHeight !== undefined ? Math.min(height, maxHeight) : height;
  const top = placement === 'above'
    ? anchorRect.top - gap - boxHeight
    : anchorRect.bottom + gap;

  const arrowLeft = Math.min(Math.max(anchorCentre - left, 10), width - 10);

  return { top, left, placement, arrowLeft, maxHeight };
}
